import { IEmployeeDetails } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { IHomeMetric } from "@gtmhub/home/services/home";
import { GoalsChangedEventArgs, IMetricChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { IMetricFormParams } from "@gtmhub/okrs/metrics/controllers/metric-form";
import { IPlanningSession } from "@gtmhub/sessions";
import { ITask } from "@gtmhub/tasks";
import { ITeam } from "@gtmhub/teams";
import { IdMap, toIdMap } from "@gtmhub/util";
import { StateService, TransitionService } from "@uirouter/angularjs";
import { IComponentOptions, IController, IScope } from "angular";
import { OnboardingService } from "../onboarding.service";
import { IChecklist } from "./onboarding-checklist.component";
import { IModalService } from "angular-ui-bootstrap";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { HowToStartModalOpenerService } from "../../shared/services/how-to-start-modal-opener.service";
import { UserInvitationModalService } from "@gtmhub/users/components/user-invitation-form/user-invitation-modal.service";
import { ITodoReflection } from "@webapp/reflections/models/reflections.models";

export class OnboardingHomeComponentCtrl implements IController {
  private okrEvents: OkrEvents;

  isReflectionsFeatureEnabled: boolean;
  activeSessions: IPlanningSession[];
  activeOpenSessions: IPlanningSession[];
  employee: IEmployeeDetails;
  todoItems: {
    tasks: ITask[];
    metrics: IHomeMetric[];
    goalsToApprove: IGoal[];
  };
  todoReflections: ITodoReflection[];
  myOkrs: IGoal[];
  teams: ITeam[];

  activeSessionIds: string[];
  activeSessionsMap: IdMap<IPlanningSession>;
  myTeamsOkrsCollapsed: IdMap<boolean>;
  displayOnboardingCard: {
    learnTheBasics: boolean;
    inviteTeammate: boolean;
    visitDemoAccount: boolean;
    visitMarketplace: boolean;
  };
  completeOnboardingCard: {
    inviteTeammate: boolean;
    visitMarketplace: boolean;
    visitDemoAccount: boolean;
  };
  checklist: IChecklist;
  showKrLabel: boolean;

  static $inject = [
    "$scope",
    "$transitions",
    "$state",
    "OnboardingService",
    "UIErrorHandlingService",
    "$uibModal",
    "AnalyticsService",
    "HowToStartModalOpenerService",
    "UserInvitationModalService",
  ];

  constructor(
    private $scope: IScope,
    private $transitions: TransitionService,
    private $state: StateService,
    private onboardingService: OnboardingService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $uibModal: IModalService,
    private analyticsService: AnalyticsService,
    private howToStartModalOpenerService: HowToStartModalOpenerService,
    private userInvitationModal: UserInvitationModalService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
  }

  $onInit(): void {
    this.$scope.$on("onboarding.createdDemoAccount", () => this.completeVisitDemoAccount());

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess({}, () => {
        this.checkToShowKrLabel();
      })
    );

    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.activeSessionIds = this.activeSessions.map((session) => session.id);
    this.activeSessionsMap = toIdMap(this.activeSessions);
    this.checklist = this.onboardingService.initChecklist();
    const { showCards, stepsV1 } = this.checklist;

    this.displayOnboardingCard = {
      learnTheBasics: !stepsV1.createdOkr,
      inviteTeammate: showCards.inviteTeammate,
      visitDemoAccount: showCards.visitDemoAccount,
      visitMarketplace: showCards.visitMarketplace,
    };

    this.completeOnboardingCard = {
      inviteTeammate: stepsV1.invitedTeammate,
      visitDemoAccount: stepsV1.visitedDemoAccount,
      visitMarketplace: stepsV1.visitedMarketplace,
    };

    this.initMyTeamsOkrsCollapsed();
    this.checkToShowKrLabel();
  }

  openVideoModal(): void {
    this.checklist = this.onboardingService.openVideoModal(this.checklist);
    this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
  }

  openSelectHowToStartModal(): void {
    this.howToStartModalOpenerService.openHowToStartModal([
      {
        title: "start_with_template",
        text: "we_have_crafted_hundreds_templates",
        img: require("wwwroot/img/screens/onboarding-home/visit-marketplace-inverted.svg"),
        button: {
          actionCb: () => {
            this.analyticsService.track("Create First OKR Clicked", { how_to_start: "with a template" });
            this.openGoalCreationModal({ shouldStartWithTemplate: true });
          },
          kind: "primary",
        },
      },
      {
        title: "start_on_your_own",
        text: "if_you_know_how_to_create",
        img: require("wwwroot/img/screens/onboarding-home/live-demo.svg"),
        button: {
          actionCb: () => {
            this.analyticsService.track("Create First OKR Clicked", { how_to_start: "on your own" });
            this.openGoalCreationModal({ shouldStartWithTemplate: false });
          },
          kind: "secondary",
        },
      },
    ]);
  }

  openGoalCreationModal(params: { shouldStartWithTemplate: boolean }): void {
    // We don't care which is the first session
    const externalSessionId = this.activeOpenSessions[0]?.id;
    const isOnlyOpenSession = this.activeOpenSessions.length === 1;

    this.$uibModal.open({
      windowClass: `create-first-goal ${params.shouldStartWithTemplate ? "wide" : "small"}`,
      backdropClass: "dark-backdrop",
      component: "createFirstGoalForm",
      resolve: {
        externalSessionId: () => externalSessionId,
        isOnlyOpenSession: () => isOnlyOpenSession,
        shouldStartWithTemplate: () => params.shouldStartWithTemplate,
      },
    });
  }

  createKr(): void {
    // This method is visible only if there is one Goal
    const metricFormParams: Partial<IMetricFormParams> = {
      id: this.myOkrs[0].id,
    };

    this.$state.go("gtmhub.home._createMetric.createMetric", metricFormParams);
  }

  visitMarketplace(): void {
    this.checklist = this.onboardingService.visitMarketplace(this.checklist);
    this.completeOnboardingCard.visitMarketplace = true;
    this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
  }

  createDemoAccount(): void {
    this.onboardingService.createDemoAccount(this.checklist).then(
      (result) => {
        this.checklist = result;
        this.completeVisitDemoAccount();
        this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  removeOnboardingCard(onboardingCard: keyof OnboardingHomeComponentCtrl["displayOnboardingCard"]): void {
    this.displayOnboardingCard[onboardingCard] = false;
    this.checklist.showCards[onboardingCard] = false;
    this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
  }

  openInvitationModal(): void {
    this.userInvitationModal.open();
  }

  private initMyTeamsOkrsCollapsed(): void {
    this.myTeamsOkrsCollapsed = this.teams.reduce((idMap, team) => ((idMap[team.id] = true), idMap), {} as IdMap<boolean>);
  }

  private completeVisitDemoAccount(): void {
    this.completeOnboardingCard.visitDemoAccount = true;
  }

  private onGoalsChanged(args: GoalsChangedEventArgs): void {
    const checkMetricActions = this.myOkrs.length === 1 && (args as IMetricChangedEventArgs).detail.goalId === this.myOkrs[0].id;

    switch (args.reason) {
      case "goalCreated":
        this.displayOnboardingCard.learnTheBasics = false;

        if (!this.myOkrs.length) {
          // We want to push only the first goal and home-my-okrs will be inited where is the logic for the OKRs
          this.myOkrs.push(args.detail.goal);
        }
        break;

      case "metricCreated":
        if (checkMetricActions) {
          if (this.myOkrs[0].metrics) {
            this.myOkrs[0].metrics.push(args.detail.metric);
          } else {
            this.myOkrs[0].metrics = [args.detail.metric];
          }
        }
        break;

      case "metricDeleted":
        if (checkMetricActions) {
          this.myOkrs[0].metrics = this.myOkrs[0].metrics.filter((metric) => metric.id !== args.detail.metricId);
        }
        break;
    }

    this.checkToShowKrLabel();
  }

  private checkToShowKrLabel(): void {
    const isOnlyOneOKR = this.myOkrs?.length === 1;
    const goalHasNoMetrics = !this.myOkrs[0]?.metrics?.length;
    const isNotGoalState = !this.$state.current.name.includes("._goalSidebar.goal");

    this.showKrLabel = isOnlyOneOKR && goalHasNoMetrics && isNotGoalState;
  }
}

export const OnboardingHomeComponent: IComponentOptions = {
  controller: OnboardingHomeComponentCtrl,
  templateUrl: require("./onboarding-home.jade"),
  bindings: {
    activeSessions: "<",
    activeOpenSessions: "<",
    employee: "<",
    todoItems: "<",
    todoReflections: "<",
    myOkrs: "<",
    teams: "<",
    isReflectionsFeatureEnabled: "<",
  },
};
